import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { API_BASE_URL } from "../../config";

// The review table totalled `proposed_rate`, a number the customer typed into
// the cart, while the server charges `Device.daily_rate / 24 x hours`. Both the rate
// and the line total now come from the cart response, so what is shown here is
// what the card is charged.
//
// The batch endpoint is all-or-nothing: it rejects the whole set rather than
// creating some of them, which is why a 409 needs showing in full rather than
// being retried silently.

function money(value) {
    if (value === null || value === undefined) return "—";
    const n = Number(value);
    return isNaN(n) ? "—" : `$${n.toFixed(2)}`;
}

function readable(value) {
    if (!value) return "—";
    const d = new Date(value);
    return isNaN(d) ? "—" : d.toLocaleDateString();
}

export default function DeviceRequestCheckout() {
    const navigate = useNavigate();

    const [cart, setCart] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [paymentOption, setPaymentOption] = useState("saved");
    const [paymentMethodId, setPaymentMethodId] = useState("");
    const [status, setStatus] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const stripe = useStripe();
    const elements = useElements();
    const token = localStorage.getItem("token");

    useEffect(() => {
        fetch(`${API_BASE_URL}/api/customer/cart`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((res) => res.json())
            .then((data) => setCart(Array.isArray(data) ? data : []))
            .catch(() => setCart([]));

        fetch(`${API_BASE_URL}/api/customer/payment-methods`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((res) => res.json())
            .then((data) => setPaymentMethods(data.payment_methods || []))
            .catch(() => setPaymentMethods([]));
    }, [token]);

    const total = cart.reduce((sum, item) => sum + (Number(item.line_total) || 0), 0);
    const blocked = cart.filter((item) => !item.bookable);

    async function handleSubmit(e) {
        e.preventDefault();
        setError("");
        setStatus("");

        if (cart.length === 0) {
            setError("Your cart is empty.");
            return;
        }
        if (blocked.length > 0) {
            setError("Some screens in your cart cannot be booked. Go back and fix them first.");
            return;
        }

        setSubmitting(true);
        let paymentMethodToUse = paymentMethodId;

        if (paymentOption === "new") {
            const cardElement = elements.getElement(CardElement);
            const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
                type: "card",
                card: cardElement,
            });
            if (stripeError) {
                setError(stripeError.message);
                setSubmitting(false);
                return;
            }
            paymentMethodToUse = paymentMethod.id;
        } else if (!paymentMethodId) {
            setError("Choose a saved card, or enter a new one.");
            setSubmitting(false);
            return;
        }

        // `proposed_rate` is deliberately not sent. The server prices from the
        // device row and ignores anything supplied here; including it only
        // suggested the client had a say.
        const batchPayload = {
            requests: cart.map((item) => ({
                device_id: item.device_id,
                image_id: item.image_id,
                start_time: item.start_time,
                end_time: item.end_time,
            })),
            payment_method_id: paymentMethodToUse,
        };

        try {
            const res = await fetch(`${API_BASE_URL}/api/customer/requests/batch`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(batchPayload),
            });
            const data = await res.json().catch(() => ({}));

            if (res.ok) {
                setStatus("Booked. Your card has been charged and each host has been asked to approve.");
                setSuccess(true);
                await fetch(`${API_BASE_URL}/api/customer/cart/clear`, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}` },
                });
                setCart([]);
            } else {
                let message = data.message || "The booking could not be completed.";
                if (Array.isArray(data.conflicting_days) && data.conflicting_days.length) {
                    message += ` Days already taken: ${data.conflicting_days.join(", ")}.`;
                }
                // Nothing was created and nothing was charged -- the batch is
                // rejected as a whole rather than partially fulfilled.
                setError(`${message} Nothing was booked and your card was not charged.`);
            }
        } catch {
            setError("Network error. Nothing was booked.");
        }
        setSubmitting(false);
    }

    if (success) {
        return (
            <div style={{ maxWidth: 640, margin: "2rem auto" }}>
                <h2>Checkout</h2>
                <p style={{ color: "#2ecc40" }}>{status}</p>
                {/* /customer/dashboard is not a route -- the bookings list is at
                    /customer/device-requests, which is what the nav calls
                    "Bookings". The old target matched nothing and rendered a
                    blank page. */}
                <button
                    type="button"
                    className="wb-btn wb-btn--primary"
                    onClick={() => navigate("/customer/device-requests")}
                >
                    Go to my bookings
                </button>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: 760, margin: "2rem auto" }}>
            <h2>Checkout</h2>
            {cart.length === 0 ? (
                <div>Your cart is empty.</div>
            ) : (
                <form onSubmit={handleSubmit}>
                    <h3>Review</h3>
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ background: "#ea476e22" }}>
                                    <th style={{ padding: "0.5rem", textAlign: "left" }}>Screen</th>
                                    <th style={{ padding: "0.5rem", textAlign: "left" }}>Dates</th>
                                    <th style={{ padding: "0.5rem", textAlign: "right" }}>Rate</th>
                                    <th style={{ padding: "0.5rem", textAlign: "right" }}>Hours</th>
                                    <th style={{ padding: "0.5rem", textAlign: "right" }}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cart.map((item) => (
                                    <tr key={item.id} style={{ borderBottom: "1px solid #eee" }}>
                                        <td style={{ padding: "0.5rem" }}>
                                            {item.business_name || `Screen #${item.device_id}`}
                                            <div style={{ color: "#888", fontSize: "0.85rem" }}>{item.address}</div>
                                        </td>
                                        <td style={{ padding: "0.5rem" }}>
                                            {readable(item.start_time)} – {readable(item.end_time)}
                                        </td>
                                        <td style={{ padding: "0.5rem", textAlign: "right" }}>
                                            {item.hourly_rate || money(item.daily_rate)}
                                            <div style={{ color: "#888", fontSize: "0.8rem" }}>
                                                per hour
                                            </div>
                                        </td>
                                        <td style={{ padding: "0.5rem", textAlign: "right" }}>{item.hours ?? "—"}</td>
                                        <td style={{ padding: "0.5rem", textAlign: "right", fontWeight: 600 }}>
                                            {money(item.line_total)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={4} style={{ padding: "0.75rem 0.5rem", textAlign: "right", fontWeight: 600 }}>
                                        You will be charged
                                    </td>
                                    <td style={{ padding: "0.75rem 0.5rem", textAlign: "right", fontWeight: 700, fontSize: "1.1rem" }}>
                                        {money(total)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {blocked.length > 0 && (
                        <div style={{ color: "#ea476e", marginTop: 12 }}>
                            Some screens in your cart cannot be booked yet. Go back to the cart to fix them.
                        </div>
                    )}

                    <h3 style={{ marginTop: "2rem" }}>Payment</h3>
                    <div style={{ display: "flex", gap: 20, marginBottom: 12 }}>
                        <label>
                            <input
                                type="radio"
                                name="paymentOption"
                                checked={paymentOption === "saved"}
                                onChange={() => setPaymentOption("saved")}
                            />{" "}
                            Saved card
                        </label>
                        <label>
                            <input
                                type="radio"
                                name="paymentOption"
                                checked={paymentOption === "new"}
                                onChange={() => setPaymentOption("new")}
                            />{" "}
                            New card
                        </label>
                    </div>

                    {paymentOption === "saved" &&
                        (paymentMethods.length === 0 ? (
                            <div style={{ color: "#888" }}>
                                No saved cards. Choose “New card”, or add one from your profile.
                            </div>
                        ) : (
                            <select value={paymentMethodId} onChange={(e) => setPaymentMethodId(e.target.value)}>
                                <option value="">Select a card</option>
                                {paymentMethods
                                    .filter((pm) => pm && pm.card)
                                    .map((pm) => (
                                        <option key={pm.id} value={pm.id}>
                                            {pm.card.brand} •••• {pm.card.last4} (exp {pm.card.exp_month}/{pm.card.exp_year})
                                        </option>
                                    ))}
                            </select>
                        ))}

                    {paymentOption === "new" && (
                        <div style={{ padding: "10px", border: "1px solid #ddd", borderRadius: 4, maxWidth: 420 }}>
                            <CardElement />
                        </div>
                    )}

                    <div style={{ marginTop: 24 }}>
                        <button
                            type="submit"
                            disabled={submitting || blocked.length > 0}
                            style={{
                                padding: "0.6rem 1.5rem",
                                background: submitting || blocked.length > 0 ? "#ccc" : "#ea476e",
                                color: "#fff",
                                border: "none",
                                borderRadius: 4,
                                fontSize: "1rem",
                                cursor: submitting || blocked.length > 0 ? "default" : "pointer",
                            }}
                        >
                            {submitting ? "Charging…" : `Pay ${money(total)}`}
                        </button>
                    </div>

                    {error && <div style={{ marginTop: 16, color: "#ea476e" }}>{error}</div>}
                    {status && !error && <div style={{ marginTop: 16, color: "#2ecc40" }}>{status}</div>}
                </form>
            )}
        </div>
    );
}
